import React, { createContext, useContext, useState, useEffect } from 'react';
import { prefixKey } from '@/utils/storagePrefix';

type Mode = 'live' | 'demo';

interface ModeContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
  isDemo: boolean;
  isLive: boolean;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

const MODE_KEY = prefixKey('app-mode');

export function ModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<Mode>(() => {
    const stored = localStorage.getItem(MODE_KEY);
    return (stored === 'live' || stored === 'demo') ? stored : 'live';
  });

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  const setMode = (newMode: Mode) => {
    localStorage.setItem(MODE_KEY, newMode);
    setModeState(newMode);
  };

  const value: ModeContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextType {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}
